import * as React from 'react';
import { Button } from './button/Button';
import { Flex } from './compositeComponents/FlexHelpers';
import { Table, TableRowWithOnClick } from './Table';


type PageSectionCellProps = {
    children: React.ReactNode,
    centered?: boolean,
}

export function PageSectionCell(props: PageSectionCellProps): JSX.Element
{
    return <>{props.children}</>;
}

type PageSectionRowProps = {
    onClick?: () => void,
    children: React.ReactElement<PageSectionCellProps> | React.ReactElement<PageSectionCellProps>[],
}

export function PageSectionRow(props: PageSectionRowProps): JSX.Element
{
    return <>{props.children}</>;
}

type PageSectionTableProps = {
    headerColumns: {
        text: string,
        width?: string,
        centered?: boolean,
    }[],
    noContentText: string,
    showAllLimit: number,
    children: React.ReactElement<PageSectionRowProps> | React.ReactElement<PageSectionRowProps>[],
}

function isPageSectionCell(value: React.ReactNode): value is React.ReactElement<PageSectionCellProps>
{
    return React.isValidElement(value) && value.type === PageSectionCell;
}

export function PageSectionTable(props: PageSectionTableProps): JSX.Element
{
    const [showAll, setShowAll] = React.useState<boolean>(false);

    const allRows = React.Children.toArray(props.children) as React.ReactElement<PageSectionRowProps>[];

    const firstBatch = allRows.slice(0, props.showAllLimit);

    let secondBatch: React.ReactElement<PageSectionRowProps>[] = [];
    if (allRows.length > firstBatch.length) {
        secondBatch = allRows.slice(props.showAllLimit);
    }

    function renderRow(row: React.ReactElement<PageSectionRowProps>): JSX.Element {
        const cells = React.Children.toArray(row.props.children);

        return (
            <TableRowWithOnClick key={row.key} onClick={row.props.onClick}>
                {cells.map((cell, cellIndex) => {
                    if (!isPageSectionCell(cell)) {
                        throw new Error('PageSectionRow children must be PageSectionCell elements');
                    }
                    return (
                        <td key={cellIndex}>
                            {cell.props.centered
                                ? <Flex justifyContent="center">{cell.props.children}</Flex>
                                : cell.props.children}
                        </td>
                    );
                })}
            </TableRowWithOnClick>
        );
    }

    return (
        <Table>
            <thead>
                <tr>
                    {props.headerColumns.map((column, index) => <th key={index} style={{ width: column.width, textAlign: column.centered ? 'center' : undefined }}>{column.text}</th>)}
                </tr>
            </thead>
            <tbody>
                {firstBatch.length === 0 &&
                    <tr>
                        <td colSpan={props.headerColumns.length}>{props.noContentText}</td>
                    </tr>
                }

                {firstBatch.map(renderRow)}

                {showAll && secondBatch.map(renderRow)}

                {secondBatch.length > 0 &&
                    <tr>
                        <td colSpan={props.headerColumns.length} style={{ textAlign: 'center' }}>
                            <Button
                                variant="text"
                                text={showAll ? "Vis mindre" : "Vis alle"}
                                icon={showAll ? "arrows_chevron_chevronUp" : "arrows_chevron_chevronDown"}
                                onClick={() => setShowAll(current => !current)}
                            />
                        </td>
                    </tr>
                }
            </tbody>
        </Table>
    );
}
